import { getAccessToken } from '../hooks/useAuth';
import type {
  IntakeResponse,
  Issue,
  IssueDetail,
  IssueList,
  Severity,
  Status,
} from './types';

import { ISSUE_API_BASE } from '../config';
const API_BASE = ISSUE_API_BASE;

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = getAccessToken();
  const headers = new Headers(init.headers);
  if (token) headers.set('Authorization', `Bearer ${token}`);
  if (init.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  const res = await fetch(`${API_BASE}${path}`, { ...init, headers });
  if (!res.ok) {
    const detail = await res.text();
    throw new Error(`${res.status} ${res.statusText}: ${detail}`);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export interface ListFilters {
  status?: Status;
  severity?: Severity;
  component?: string;
  assignee?: string;
  q?: string;
  limit?: number;
  offset?: number;
}

export const api = {
  intake: (text: string, screenshot_storage_id?: number) =>
    request<IntakeResponse>('/api/v1/issues/intake', {
      method: 'POST',
      body: JSON.stringify({ text, screenshot_storage_id }),
    }),

  list: (filters: ListFilters = {}) => {
    const params = new URLSearchParams();
    for (const [k, v] of Object.entries(filters)) {
      if (v !== undefined && v !== '' && v !== null) params.set(k, String(v));
    }
    const q = params.toString();
    return request<IssueList>(`/api/v1/issues${q ? `?${q}` : ''}`);
  },

  get: (id: number) => request<IssueDetail>(`/api/v1/issues/${id}`),

  patch: (id: number, fields: Partial<Issue>) =>
    request<Issue>(`/api/v1/issues/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(fields),
    }),

  resolve: (
    id: number,
    body: { resolution_commit?: string; resolution_summary?: string; status?: Status }
  ) =>
    request<Issue>(`/api/v1/issues/${id}/resolve`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  comment: (id: number, body: string) =>
    request<Comment>(`/api/v1/issues/${id}/comments`, {
      method: 'POST',
      body: JSON.stringify({ body }),
    }),
};
